<template>
  <div class="page" v-loading="loading">
    <div class="card">
      <div class="title-row">
        <div class="title">测评详情</div>
        <div class="actions">
          <el-checkbox v-if="item && item.ai_summary" v-model="includeAiSummary" style="margin-right: 10px">包含AI解读</el-checkbox>
          <el-button size="mini" type="primary" @click="printReport" style="margin-right: 10px" v-if="item && item.status === 'COMPLETED'">🖨️ 打印报告</el-button>
          <el-button 
            size="mini" 
            type="success" 
            @click="handleGenerateSummary" 
            :loading="generating"
            style="margin-right: 10px"
            v-if="item && item.status === 'COMPLETED' && !item.ai_summary"
          >✨ 生成AI解读</el-button>
          <el-button size="mini" @click="$router.push('/app/assessments')">返回列表</el-button>
        </div>
      </div>

      <div class="meta-grid" v-if="item">
        <div class="meta-item"><span class="k">姓名</span><span class="v">{{ item.visitor_name }}</span></div>
        <div class="meta-item"><span class="k">性别</span><span class="v">{{ item.visitor_gender }}</span></div>
        <div class="meta-item"><span class="k">年龄</span><span class="v">{{ item.visitor_age }}岁</span></div>
        <div class="meta-item"><span class="k">就诊号</span><span class="v">{{ item.visitor_number }}</span></div>
        <div class="meta-item"><span class="k">量表</span><span class="v">{{ item.scale_title }}</span></div>
        <div class="meta-item"><span class="k">状态</span><span class="v">{{ formatStatus(item.status) }}</span></div>
        <div class="meta-item"><span class="k">创建时间</span><span class="v">{{ formatDateTime(item.created_at) }}</span></div>
      </div>

      <div class="share" v-if="item && item.status === 'PENDING'">
        <div class="k">来访者测评链接（uid）</div>
        <div class="share-row">
          <el-input :value="publicUrl" readonly />
          <el-button type="primary" @click="copy(publicUrl)">复制</el-button>
          <el-button @click="openPublic">打开</el-button>
        </div>
        <div class="qrcode-container">
          <canvas ref="qrcodeCanvas"></canvas>
          <div class="hint">请使用手机扫描二维码开始测评</div>
        </div>
      </div>

      <div class="section" v-if="item && item.status === 'COMPLETED' && item.dimension_scores">
        <div class="section-title">维度分结果</div>
        <div class="table-container">
          <el-table :data="dimensionList" stripe class="dimension-table" :header-cell-style="{background:'var(--table-header-bg)', color:'var(--text-secondary)', textAlign:'center'}">
            <el-table-column min-width="1"></el-table-column>
            <el-table-column prop="name" label="维度名称" width="400" align="center">
              <template slot-scope="{ row }">
                <span class="dimension-name">{{ row.name }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="score" label="得分" width="300" align="center">
              <template slot-scope="{ row }">
                <span class="score-val">{{ row.score }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="method" label="计算方式" width="300" align="center">
              <template slot-scope="{ row }">
                <el-tag size="small" type="info" effect="light" class="method-tag">{{ formatMethod(row.method) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column min-width="1"></el-table-column>
          </el-table>
        </div>
      </div>
            <div class="section clinical-section" v-if="item && item.status === 'COMPLETED'">
        <div class="section-title" style="border-left-color: #f56c6c;">
          AI 临床辅助评估 (咨询师专用)
          <el-tag type="danger" size="mini" effect="dark" style="margin-left: 10px; vertical-align: middle;">内部专用</el-tag>
        </div>
        
        <div v-if="!item.ai_clinical_response" class="clinical-empty">
           <div class="empty-tip">暂无临床辅助评估数据。此部分内容仅供咨询师参考，不会显示在来访者报告中。</div>
           <el-button 
            type="danger" 
            plain
            size="small" 
            @click="handleGenerateClinicalSummary" 
            :loading="generatingClinical"
          >🛡️ 生成临床辅助评估</el-button>
        </div>

        <div v-else>
          <div class="clinical-content markdown-body" v-html="parsedClinicalSummary"></div>
          <div class="ai-meta">生成时间：{{ formatDateTime(item.ai_clinical_generated_at) }}</div>
        </div>
      </div>

      <div class="section" v-if="item && item.status === 'COMPLETED' && item.report_data">
        <div class="section-title">测评报告</div>
        <div v-if="reportList.length === 0" class="empty-text">暂无报告数据</div>
        <div v-for="rpt in reportList" :key="rpt.code" class="report-card">
          <div class="report-header">
            <span class="report-name">{{ rpt.name }}</span>
            <div class="report-score-badge" v-if="rpt.score">
              <span class="s-label">得分</span>
              <span class="s-value">{{ rpt.score }}</span>
            </div>
          </div>
          <div class="report-body">
            <div class="report-row" v-if="rpt.result">
              <div class="report-label">📝 结果解释</div>
              <div class="report-content">{{ rpt.result }}</div>
            </div>
            <div class="report-row" v-if="rpt.suggestion">
              <div class="report-label">💡 建议</div>
              <div class="report-content">
                <div v-for="(line, idx) in rpt.suggestion.split('\n')" :key="idx" class="sug-line">
                  {{ line }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="section" v-if="item && item.ai_summary">
        <div class="section-title">AI 综合解读</div>
        <div class="ai-summary-content markdown-body" v-html="parsedAiSummary"></div>
        <div class="ai-meta">生成时间：{{ formatDateTime(item.ai_generated_at) }}</div>
      </div>

      <div class="section" v-if="item && item.status === 'COMPLETED'">
        <div class="section-title">作答明细</div>
        <el-table :data="item.answers || []" stripe>
          <el-table-column label="题号" width="80">
            <template slot-scope="{ row }">{{ row.question_order || row.question_id }}</template>
          </el-table-column>
          <el-table-column prop="question_content" label="题目" min-width="240" />
          <el-table-column prop="option_text" label="选项" width="140" />
          <el-table-column label="文本" min-width="160">
            <template slot-scope="{ row }">{{ row.question_widget_type === 'text' ? row.text_value : '-' }}</template>
          </el-table-column>
          <el-table-column prop="score" label="得分" width="80" />
        </el-table>
      </div>
    </div>

    <el-dialog :visible.sync="agreementDialogVisible" :title="agreementTitle || '使用协议'" width="620px">
      <div style="white-space: pre-wrap; line-height: 1.7;">{{ agreementContent }}</div>
      <div style="margin-top: 14px;">
        <el-checkbox v-model="agreementChecked">我已阅读并同意以上协议</el-checkbox>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="agreementDialogVisible = false">取消</el-button>
        <el-button type="primary" :disabled="!agreementChecked" :loading="agreementLoading" @click="acceptAgreementAndRetry">同意并继续</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getAssessment, generateAssessmentSummary } from '@/api/assessments'
import { getAgreementCurrent, acceptAgreements } from '@/api/settings'
import QRCode from 'qrcode'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { notifyApiError } from '@/utils/notifyApiError'

export default {
  name: 'AssessmentDetail',
  props: ['id'],
  data() {
    return {
      loading: false,
      generating: false,
      generatingClinical: false,
      includeAiSummary: true,
      agreementDialogVisible: false,
      agreementLoading: false,
      agreementChecked: false,
      agreementKey: '',
      agreementVersion: '',
      agreementChecksum: '',
      agreementTitle: '',
      agreementContent: '',
      pendingTargetType: null,
      item: null,
    }
  },
  computed: {
    parsedAiSummary() {
      if (!this.item || !this.item.ai_summary) return ''
      return DOMPurify.sanitize(marked.parse(this.item.ai_summary))
    },
    parsedClinicalSummary() {
      if (!this.item || !this.item.ai_clinical_response) return ''
      return DOMPurify.sanitize(marked.parse(this.item.ai_clinical_response))
    },
    publicUrl() {
      if (!this.item || !this.item.uid) return ''
      const base = window.location.origin + window.location.pathname
      return `${base}#/public/${this.item.uid}`
    },
    dimensionList() {
      if (!this.item || !this.item.dimension_scores) return []
      const list = Object.entries(this.item.dimension_scores).map(([code, val]) => ({
        code,
        ...val,
      }))
      // Sort by order if available
      return list.sort((a, b) => {
        const orderA = a.order !== undefined ? a.order : 9999
        const orderB = b.order !== undefined ? b.order : 9999
        return orderA - orderB
      })
    },
    reportList() {
      if (!this.item || !this.item.report_data) return []
      const scores = this.item.dimension_scores || {}
      const list = Object.entries(this.item.report_data).map(([code, val]) => {
        const dimInfo = scores[code]
        const name = dimInfo ? dimInfo.name : code
        return {
          code,
          name,
          result: val.result,
          suggestion: val.suggestion,
          score: val.score,
          order: dimInfo && dimInfo.order !== undefined ? dimInfo.order : 9999
        }
      })
      
      // Sort by order
      return list.sort((a, b) => a.order - b.order)
    },
  },
  watch: {
    item: {
      handler(val) {
        if (val && val.status === 'PENDING') {
          this.$nextTick(this.generateQRCode)
        }
      },
      immediate: true
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    async showAgreementDialog(requiredItem) {
      const key = requiredItem?.key
      if (!key) return

      this.agreementLoading = true
      try {
        const resp = await getAgreementCurrent(key)
        const data = resp.data
        this.agreementKey = data.key
        this.agreementVersion = data.version
        this.agreementChecksum = data.checksum
        this.agreementTitle = data.title
        this.agreementContent = data.content
        this.agreementChecked = false
        this.agreementDialogVisible = true
      } catch (e) {
        console.error(e)
        notifyApiError(e, '获取协议失败，请稍后重试')
      } finally {
        this.agreementLoading = false
      }
    },
    async acceptAgreementAndRetry() {
      if (!this.agreementChecked || !this.agreementKey) return
      this.agreementLoading = true
      try {
        await acceptAgreements([
          {
            key: this.agreementKey,
            version: this.agreementVersion,
            checksum: this.agreementChecksum,
          },
        ])
        this.agreementDialogVisible = false

        const targetType = this.pendingTargetType
        this.pendingTargetType = null
        if (targetType === 'visitor') {
          const data = await this.generateSummaryDirect('visitor')
          this.showSuccessWithUsage('AI 解读生成成功', data)
        } else if (targetType === 'clinical') {
          const data = await this.generateSummaryDirect('clinical')
          this.showSuccessWithUsage('临床辅助评估生成成功', data)
        }
      } catch (e) {
        console.error(e)
        const msg = e.response?.data?.detail || '签署失败，请稍后重试'
        notifyApiError(e, msg)
      } finally {
        this.agreementLoading = false
      }
    },
    async generateSummaryDirect(targetType) {
      const resp = await generateAssessmentSummary(this.item.id, targetType)
      if (targetType === 'visitor') {
        const { ai_summary, ai_generated_at } = resp.data
        this.item = { ...this.item, ai_summary, ai_generated_at }
        this.includeAiSummary = true
      } else {
        const { ai_clinical_response, ai_clinical_generated_at } = resp.data
        this.item = { ...this.item, ai_clinical_response, ai_clinical_generated_at }
      }
      return resp.data
    },
    getAiUsageText(data) {
      const usage = data?.usage || {}
      const stats = data?.stats || {}
      const totalTokens = usage.total_tokens
      const todayRemaining = stats.today_remaining
      const todayLimit = stats.today_limit
      const monthUsed = stats.month_used_tokens
      const monthLimit = stats.month_limit_tokens

      const parts = []
      if (typeof totalTokens === 'number') parts.push(`本次消耗 ${totalTokens} tokens`)
      if (typeof todayRemaining === 'number' && typeof todayLimit === 'number') {
        parts.push(`今日剩余 ${todayRemaining}/${todayLimit} 次`)
      }
      if (typeof monthUsed === 'number' && typeof monthLimit === 'number') {
        parts.push(`本月已用 ${monthUsed}/${monthLimit} tokens`)
      }
      return parts.join('；')
    },
    showSuccessWithUsage(title, data) {
      const usageText = this.getAiUsageText(data)
      const msg = usageText ? `${title}；${usageText}` : title
      const len = String(msg || '').length
      const duration = Math.min(12000, Math.max(6000, 3500 + len * 35))
      this.$message({
        type: 'success',
        message: msg,
        duration,
      })
    },
    async generateQRCode() {
      if (!this.item || !this.publicUrl) return
      try {
        // Find the canvas ref
        const canvas = this.$refs.qrcodeCanvas
        if (!canvas) return
        await QRCode.toCanvas(canvas, this.publicUrl, { width: 200, margin: 1 })
      } catch (err) {
        console.error('QR Code generation failed', err)
      }
    },
    formatStatus(v) {
      if (v === 'PENDING') return '待测评'
      if (v === 'COMPLETED') return '已完成'
      if (v === 'CANCELED') return '已取消'
      return v || ''
    },
    statusType(v) {
      if (v === 'PENDING') return 'warning'
      if (v === 'COMPLETED') return 'success'
      if (v === 'CANCELED') return 'info'
      return 'info'
    },
    formatDateTime(v) {
      if (!v) return ''
      const d = new Date(v)
      if (Number.isNaN(d.getTime())) return v
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(
        d.getMinutes()
      )}`
    },
    formatMethod(v) {
      const map = {
        SUM: '题目求和',
        AVG: '题目平均分',
        FORMULA: '公式计算',
        NORM: '常模标准转换',
      }
      return map[v] || v
    },
    async fetchData() {
      this.loading = true
      try {
        const resp = await getAssessment(this.$route.params.id)
        this.item = resp.data
      } catch (e) {
        this.item = null
        if (e && e.response && e.response.status === 401) return
        notifyApiError(e, '获取测评详情失败')
      } finally {
        this.loading = false
      }
    },
    pretty(v) {
      try {
        return JSON.stringify(v, null, 2)
      } catch (e) {
        return String(v || '')
      }
    },
    copy(text) {
      if (!text) return
      navigator.clipboard
        .writeText(text)
        .then(() => this.$message.success('已复制'))
        .catch(() => this.$message.warning('复制失败，请手动复制'))
    },
    openPublic() {
      if (!this.item || !this.item.uid) return
      this.$router.push(`/public/${this.item.uid}`)
    },
    async handleGenerateSummary() {
      if (!this.item) return
      
      try {
        await this.$confirm('生成 AI 解读将消耗一次生成额度，且生成后无法修改。确定要生成吗？', '确认生成', {
          confirmButtonText: '确定生成',
          cancelButtonText: '取消',
          type: 'info'
        })
      } catch (e) {
        return
      }

      this.generating = true
      try {
        const data = await this.generateSummaryDirect('visitor')
        this.showSuccessWithUsage('AI 解读生成成功', data)
      } catch (e) {
        console.error(e)
        const code = e.response?.data?.code
        if (code === 'AGREEMENT_REQUIRED') {
          this.pendingTargetType = 'visitor'
          await this.showAgreementDialog(e.response?.data?.required?.[0])
        } else {
          const msg = e.response?.data?.detail || '生成失败，请稍后重试'
          notifyApiError(e, msg)
        }
      } finally {
        this.generating = false
      }
    },
    async handleGenerateClinicalSummary() {
      if (!this.item) return
      
      try {
        await this.$confirm('生成 临床辅助评估 将消耗一次生成额度，且生成后无法修改。确定要生成吗？', '确认生成', {
          confirmButtonText: '确定生成',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (e) {
        return
      }

      this.generatingClinical = true
      try {
        const data = await this.generateSummaryDirect('clinical')
        this.showSuccessWithUsage('临床辅助评估生成成功', data)
      } catch (e) {
        console.error(e)
        const code = e.response?.data?.code
        if (code === 'AGREEMENT_REQUIRED') {
          this.pendingTargetType = 'clinical'
          await this.showAgreementDialog(e.response?.data?.required?.[0])
        } else {
          const msg = e.response?.data?.detail || '生成失败，请稍后重试'
          notifyApiError(e, msg)
        }
      } finally {
        this.generatingClinical = false
      }
    },
    printReport() {
      if (!this.item) return

      const settings = this.$store.state.settings || {}
      const reportTitle = settings.report_title || '心理测评结果报告'
      const reportSubtitle = settings.report_subtitle || 'PsyScale Clinical Assessment System'
      const institutionName = settings.institution_name || ''
      const reportDisclaimer = settings.report_disclaimer || ''
      const reportLogo = settings.system_logo || settings.favicon_url || '/favicon.ico'

      const styles = `
        <style>
          body { font-family: "SimSun", "Songti SC", serif; padding: 20px; max-width: 800px; margin: 0 auto; color: #000; }
          h1, h3, .report-block-title, .report-sub-title, th { font-family: "SimHei", "Heiti SC", sans-serif; }
          h1 { text-align: center; font-size: 24px; margin-bottom: 5px; font-weight: bold; page-break-after: avoid; }
          .subtitle { text-align: center; font-size: 14px; color: #666; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 10px; }
          .info { margin-bottom: 20px; border-bottom: 1px solid #eee; padding-bottom: 10px; }
          .info-table { width: 100%; border-collapse: collapse; font-size: 14px; }
          .info-table td { border: none; padding: 4px 0; text-align: left; }
          .section { margin-bottom: 25px; }
          h3 { font-size: 16px; border-left: 4px solid #000; padding-left: 10px; margin-bottom: 15px; background: #f9f9f9; line-height: 1.5; font-weight: bold; page-break-after: avoid; break-after: avoid; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #000; padding: 6px 8px; text-align: center; }
          th { background: #f0f0f0; font-weight: bold; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          thead { display: table-header-group; }
          tr { page-break-inside: avoid; break-inside: avoid; }
          .report-block { margin-bottom: 20px; page-break-inside: avoid; break-inside: avoid; border: 1px solid #ddd; padding: 10px; border-radius: 4px; }
          .ai-summary-block { margin-bottom: 20px; page-break-inside: auto; break-inside: auto; border: 1px solid #ddd; padding: 10px; border-radius: 4px; }
          .report-block-title { font-weight: bold; font-size: 14px; margin-bottom: 8px; background: #eee; padding: 5px; border-left: 3px solid #333; page-break-after: avoid; break-after: avoid; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .report-sub-title { font-weight: bold; font-size: 13px; margin: 8px 0 4px 0; color: #333; page-break-after: avoid; break-after: avoid; }
          .report-text { font-size: 13px; line-height: 1.6; text-align: justify; margin-bottom: 5px; }
          .footer { margin-top: 50px; display: flex; justify-content: space-between; font-size: 12px; }
          .disclaimer { margin-top: 20px; font-size: 10px; color: #666; text-align: center; border-top: 1px solid #eee; padding-top: 10px; }
          ul { margin: 4px 0; padding-left: 0; list-style-type: none; }
          li { margin-bottom: 4px; }
          @media print {
            @page { margin: 15mm 20mm; }
            body { padding: 0; margin: 0; }
            .no-print { display: none; }
            .report-block, .ai-summary-block { border: 1px solid #000; }
            .report-block-title { background: #f0f0f0; border-left-color: #000; }
          }
          .markdown-content p { margin: 0 0 8px 0; text-align: justify; orphans: 3; widows: 3; }
          .markdown-content strong { font-weight: bold; }
          .markdown-content ul, .markdown-content ol { padding-left: 20px; margin: 5px 0; }
          .markdown-content li { margin-bottom: 3px; }
          .markdown-content h1, .markdown-content h2, .markdown-content h3 { font-size: 14px; font-weight: bold; margin: 12px 0 6px 0; color: #333; }
          .markdown-content h1 { font-size: 15px; border-bottom: 1px dashed #ccc; padding-bottom: 4px; }
        </style>
      `

      const dimensionRows = this.dimensionList.map(d => `
        <tr>
          <td>${d.name}</td>
          <td><strong>${d.score}</strong></td>
          <td>${this.formatMethod(d.method)}</td>
        </tr>
      `).join('')

      const reportItems = this.reportList.map(r => `
        <div class="report-block">
          <div class="report-block-title">${r.name} ${r.score ? `(得分: ${r.score})` : ''}</div>
          
          ${r.result ? `
            <div class="report-sub-title">【结果解释】</div>
            <div class="report-text">${r.result}</div>
          ` : ''}
          
          ${r.suggestion ? `
            <div class="report-sub-title">【指导建议】</div>
            <div class="report-text">
              <ul>
                ${r.suggestion.split('\\n').map(l => `<li>${l}</li>`).join('')}
              </ul>
            </div>` : ''}
        </div>
      `).join('')

      const aiSection = (this.item.ai_summary && this.includeAiSummary) ? `
        <div class="section">
            <h3>三、AI 综合解读</h3>
            <div class="ai-summary-block">
                <div class="report-text markdown-content">${marked.parse(this.item.ai_summary)}</div>
            </div>
        </div>
      ` : ''

      const html = `
        <html>
        <head>
          <title>测评报告 - ${this.item.visitor_name}</title>
          ${styles}
        </head>
        <body>
          ${reportLogo ? `<div style="text-align:center;margin-bottom:10px;"><img src="${reportLogo}" style="height:50px;"></div>` : ''}
          <h1>${reportTitle}</h1>
          <div class="subtitle">${reportSubtitle}</div>
          
          <div class="info">
            <table class="info-table">
              <tr>
                <td width="25%"><strong>姓名：</strong>${this.item.visitor_name}</td>
                <td width="25%"><strong>性别：</strong>${this.item.visitor_gender || '-'}</td>
                <td width="25%"><strong>年龄：</strong>${this.item.visitor_age ? this.item.visitor_age + '岁' : '-'}</td>
                <td width="25%"><strong>就诊号：</strong>${this.item.visitor_number || '-'}</td>
              </tr>
              <tr>
                <td colspan="2"><strong>量表：</strong>${this.item.scale_title}</td>
                <td colspan="2"><strong>测评时间：</strong>${this.formatDateTime(this.item.created_at)}</td>
              </tr>
              <tr>
                 <td colspan="4"><strong>报告编号：</strong>${this.item.id}</td>
              </tr>
            </table>
          </div>

          <div class="section">
            <h3>一、维度得分</h3>
            <table>
              <thead><tr><th>维度名称</th><th>得分</th><th>计算方式</th></tr></thead>
              <tbody>${dimensionRows}</tbody>
            </table>
          </div>

          <div class="section">
            <h3>二、测评结论与建议</h3>
            ${reportItems || '<div style="color:#666; font-style:italic">暂无详细报告数据</div>'}
          </div>

          ${aiSection}

          <div class="footer">
            <div>${institutionName}</div>
            <div>打印时间：${new Date().toLocaleString()}</div>
          </div>
          
          <div class="disclaimer">
            ${reportDisclaimer || '* 本报告结果基于被试者的自评数据生成，仅供临床参考，不作为最终精神科诊断依据。<br>请结合临床访谈和其他检查结果综合判断。'}
          </div>
        </body>
        </html>
      `

      const win = window.open('', '_blank')
      win.document.write(html)
      win.document.close()
      
      // 等待资源加载后打印
      win.onload = function() {
        win.print()
      }
    },
  },
}
</script>

<style scoped>
.card {
  background: var(--bg-card);
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
}
.title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.title {
  font-size: 20px;
  font-weight: 800;
  color: var(--text-primary);
}
.meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
  margin-bottom: 20px;
}
.meta-item .k {
  display: inline-block;
  width: 84px;
  color: var(--text-secondary);
}
.meta-item .v {
  color: var(--text-primary);
  font-weight: 600;
}
.share {
  margin-bottom: 14px;
}
.share .k {
  color: var(--text-secondary);
  font-size: 13px;
  margin-bottom: 6px;
}
.share-row {
  display: flex;
  gap: 8px;
}
.qrcode-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
}
.qrcode-container .hint {
  margin-top: 8px;
  font-size: 14px;
  color: var(--text-secondary);
}
.section {
  margin-top: 32px;
}
.section-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 16px 0;
  padding-left: 12px;
  border-left: 4px solid #409eff;
  line-height: 1.2;
}
.json {
  background: #0b1220;
  color: #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  overflow: auto;
  font-size: 12px;
}
.table-container {
  margin: 16px 0;
}
.dimension-table {
  width: 100%;
}
.dimension-name {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 14px;
}
.score-val {
  font-weight: 800;
  color: #409eff;
  font-size: 18px;
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', '微软雅黑', Arial, sans-serif;
}
.method-tag {
  border: none;
  background-color: transparent;
  color: var(--text-secondary);
}
.report-card {
  background: var(--bg-body);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  margin-bottom: 12px;
  padding: 16px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
}
.report-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px -8px rgba(0, 0, 0, 0.15);
  border-color: #409eff;
  z-index: 1;
  background: var(--bg-card);
}
.report-score-badge {
  background: rgba(230, 162, 60, 0.1);
  color: #e6a23c;
  border: 1px solid rgba(230, 162, 60, 0.2);
  border-radius: 6px;
  padding: 4px 12px;
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.s-label {
  font-size: 12px;
  opacity: 0.9;
  font-weight: 500;
}
.s-value {
  font-size: 20px;
  font-weight: 800;
  font-family: 'Helvetica Neue', sans-serif;
  line-height: 1;
}
.report-header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 12px;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 8px;
  gap: 12px;
}
.report-name {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
}
.report-row {
  margin-bottom: 10px;
}
.report-row:last-child {
  margin-bottom: 0;
}
.report-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 4px;
}
.report-content {
  font-size: 14px;
  color: var(--text-regular);
  line-height: 1.5;
}
.sug-line {
  position: relative;
  padding-left: 12px;
  margin-bottom: 2px;
}
.sug-line::before {
  content: "•";
  position: absolute;
  left: 0;
  color: var(--text-secondary);
}
.empty-text {
  color: var(--text-secondary);
  font-size: 14px;
  padding: 10px 0;
}
.markdown-body {
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-primary);
  background: var(--bg-body);
  padding: 16px;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  transition: all 0.3s;
}
.dark .markdown-body {
  background: rgba(0, 0, 0, 0.2);
  border-color: rgba(255, 255, 255, 0.1);
}
::v-deep .markdown-body p {
  margin-bottom: 1em;
  color: var(--text-regular);
  line-height: 1.8;
}
::v-deep .markdown-body strong {
  font-weight: 700;
  color: #409eff;
}
::v-deep .markdown-body h1,
::v-deep .markdown-body h2,
::v-deep .markdown-body h3,
::v-deep .markdown-body h4 {
  color: var(--text-primary);
  margin-top: 1.2em;
  margin-bottom: 0.6em;
  font-weight: 700;
  line-height: 1.4;
}
::v-deep .markdown-body h1 { font-size: 1.25em; border-bottom: 1px solid var(--border-color); padding-bottom: 0.3em; }
::v-deep .markdown-body h2 { font-size: 1.15em; }
::v-deep .markdown-body h3 { font-size: 1.1em; }
::v-deep .markdown-body ul, ::v-deep .markdown-body ol {
  padding-left: 1.5em;
  margin-bottom: 1em;
  color: var(--text-regular);
}
::v-deep .markdown-body li {
  margin-bottom: 0.5em;
}
.ai-meta {
  margin-top: 12px;
  font-size: 12px;
  color: var(--text-secondary);
  text-align: right;
}
.clinical-section {
  border: 1px dashed #f56c6c;
  background: rgba(253, 226, 226, 0.2);
  border-radius: 8px;
  padding: 16px;
  margin-top: 24px;
}
.clinical-empty {
  text-align: center;
  padding: 20px 0;
}
.empty-tip {
  color: var(--text-secondary);
  font-size: 13px;
  margin-bottom: 12px;
}
.clinical-content {
  background: #fff;
}
.dark .clinical-content {
  background: rgba(0, 0, 0, 0.2);
}
</style>
